/**
 * Marca da plataforma.
 *
 * Nome e slogan vivem aqui — trocar a identidade é editar este arquivo, não
 * caçar strings pelas telas. O nome vem de VITE_APP_NAME quando definido,
 * então dá para renomear no build sem tocar no código.
 *
 * O desenho segue o kit da Malva, como a marca da Órbita: o símbolo é o
 * pódio do favicon (public/favicon.svg) — segundo, primeiro e terceiro lugar,
 * com o ponto em cima do primeiro — nos tons `--mark-*`, e o nome em semibold
 * com o espaçamento bem apertado. A aba do navegador e o canto do menu
 * mostram o mesmo desenho.
 */
export const APP_NAME = (import.meta.env.VITE_APP_NAME as string | undefined)?.trim() || 'Painel comercial';
export const APP_TAGLINE = 'Painel de controle';

/** O pódio na grade de 48 do favicon, recortado ao desenho (x 5–43, y 1–43). */
function PodiumMark({ height, showcase }: { height: number; showcase: boolean }) {
  const tone = (place: 1 | 2 | 3) => `var(--${showcase ? 'showcase-' : ''}mark-${place})`;
  return (
    <svg aria-hidden width={(height * 38) / 42} height={height} viewBox="5 1 38 42" style={{ flexShrink: 0, display: 'block' }}>
      <path d="M6 24.5 Q6 21.5 9 21.5 H17.5 V42 H6 Z" fill={tone(2)} />
      <path d="M17.5 15 Q17.5 12 20.5 12 H27.5 Q30.5 12 30.5 15 V42 H17.5 Z" fill={tone(1)} />
      <path d="M30.5 28 H39 Q42 28 42 31 V42 H30.5 Z" fill={tone(3)} />
      <circle cx="24" cy="5.5" r="3.5" fill={tone(1)} />
    </svg>
  );
}

interface WordmarkProps {
  size?: 'md' | 'lg';
  /** `showcase`: sobre a vitrine escura da tela de entrada. */
  tone?: 'default' | 'showcase';
}

export function Wordmark({ size = 'md', tone = 'default' }: WordmarkProps) {
  const large = size === 'lg';
  const onShowcase = tone === 'showcase';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: large ? 11 : 9 }}>
      <span aria-hidden style={{ display: 'flex', justifyContent: 'center', width: large ? 24 : 20, flexShrink: 0 }}>
        <PodiumMark height={large ? 26 : 22} showcase={onShowcase} />
      </span>
      {/* Classes para o layout: o menu recolhido esconde o texto; a barra do celular, o slogan. */}
      <span className="wordmark-text" style={{ minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: large ? 22 : 18,
            fontWeight: 600,
            lineHeight: 1.05,
            letterSpacing: '-0.045em',
            color: onShowcase ? 'var(--on-showcase)' : 'var(--ink)',
          }}
        >
          {APP_NAME}
        </span>
        <span
          className="wordmark-tagline"
          style={{
            display: 'block',
            fontSize: large ? 11 : 10.5,
            fontWeight: 600,
            color: onShowcase ? 'var(--on-showcase-soft)' : 'var(--ink-faint)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginTop: large ? 4 : 3,
          }}
        >
          {APP_TAGLINE}
        </span>
      </span>
    </div>
  );
}
